import type { AddOnSetIdInput, AddOnSetListItem } from '@/lib/services/add-on-sets/addOnSets.types'
import type { ProductAddOnSet } from '@/lib/services/products/products.types'
import SearchInput from '@/components/form/SearchInput'
import {
  hasSelection,
  toSelectionKey,
  uniqueSelections,
  withoutSelections,
} from '@/components/products/selectionIds'
import { sortSympathyFuneralFirst } from '@/components/products/sortSelectableItems'
import { useAddOnSets } from '@/lib/services/add-on-sets/addOnSets.query'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import LoadingSpinner from '@/components/ui/loading-spinner'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Typography } from '@/components/ui/typography'
interface AddOnSetPickerProps<TInitial extends { id: AddOnSetIdInput }> {
  organizationId?: number | string
  initialData?: TInitial[]
  onConfirm?: (sets: (TInitial | AddOnSetListItem)[]) => void
  onCancel?: () => void
}

export default function AddOnSetPicker<TInitial extends { id: AddOnSetIdInput } = ProductAddOnSet>({
  organizationId,
  initialData = [],
  onConfirm,
  onCancel,
}: AddOnSetPickerProps<TInitial>) {
  const [search, setSearch] = useState('')
  const { data: addOnSets = [], isLoading, isError } = useAddOnSets({ organizationId })
  const [selected, setSelected] = useState<AddOnSetIdInput[]>(() =>
    initialData.map((set) => set.id)
  )
  const normalizedSearch = search.trim().toLowerCase()
  const filteredSets = sortSympathyFuneralFirst(
    !normalizedSearch
      ? addOnSets
      : addOnSets.filter((set) => set?.name?.toLowerCase()?.includes(normalizedSearch))
  )
  const filteredSetIds = filteredSets.map((set) => set.id)
  const filteredSelectedCount = filteredSetIds.filter((id) => hasSelection(selected, id)).length
  const hasFilteredSets = filteredSetIds.length > 0
  const selectAllState =
    hasFilteredSets && filteredSelectedCount === filteredSetIds.length
      ? true
      : filteredSelectedCount > 0
        ? 'indeterminate'
        : false
  const hasAllVisibleSelection = hasFilteredSets && filteredSelectedCount === filteredSetIds.length
  const selectAllActionLabel = hasAllVisibleSelection ? 'Select none' : 'Select all'
  const setVisibleSelection = (checked: boolean | 'indeterminate') => {
    if (!hasFilteredSets) {
      return
    }

    if (checked === true) {
      setSelected((previous) => uniqueSelections([...previous, ...filteredSetIds]))
      return
    }

    setSelected((previous) => withoutSelections(previous, filteredSetIds))
  }

  return (
    <div className="space-y-4">
      <SearchInput placeholder="Search Add-on Collections..." value={search} setValue={setSearch} />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Label appearance="choice">
            <Checkbox
              checked={selectAllState}
              disabled={!hasFilteredSets}
              onCheckedChange={setVisibleSelection}
            />
            <span>{selectAllActionLabel}</span>
          </Label>
          <Typography variant="label" tone="muted" align="right" className="ml-auto">
            {selected.length} Add-on Collection{selected.length === 1 ? '' : 's'} selected
          </Typography>
        </div>

        <div className="rounded-xl border bg-muted/15">
          <ScrollArea className="h-[360px]">
            {isLoading ? (
              <div className="flex h-full min-h-[220px] items-center justify-center p-6 text-center">
                <LoadingSpinner className="mr-2" /> Loading Add-on Collections...
              </div>
            ) : isError ? (
              <div className="flex h-full min-h-[220px] items-center justify-center p-6 text-center">
                <Typography variant="supporting">Unable to load Add-on Collections.</Typography>
              </div>
            ) : filteredSets.length > 0 ? (
              <ul className="px-3 py-2">
                {filteredSets.map((set, index) => (
                  <li key={set.id}>
                    <Label htmlFor={`add-on-set-${set.id}`} appearance="choiceRow">
                      <Checkbox
                        id={`add-on-set-${set.id}`}
                        checked={hasSelection(selected, set.id)}
                        onCheckedChange={(isChecked) => {
                          if (isChecked) {
                            setSelected((previous) => uniqueSelections([...previous, set.id]))
                          } else {
                            setSelected((previous) => withoutSelections(previous, [set.id]))
                          }
                        }}
                      />
                      <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
                        <span className="min-w-0 truncate font-medium">{set.name}</span>
                        <Typography
                          variant="label"
                          tone="muted"
                          align="right"
                          className="ml-auto shrink-0 whitespace-nowrap"
                        >
                          {set.add_ons_count ?? set.add_ons?.length ?? 0} add-on
                          {(set.add_ons_count ?? set.add_ons?.length ?? 0) === 1 ? '' : 's'}
                        </Typography>
                      </div>
                    </Label>
                    {index !== filteredSets.length - 1 ? <Separator /> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex h-full min-h-[220px] items-center justify-center p-6 text-center">
                <Typography variant="supporting">
                  No Add-on Collections found for that search.
                </Typography>
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => onCancel?.()}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            const selectedIds = new Set((selected ?? []).map(toSelectionKey))
            const existingIds = new Set(
              (initialData?.map((set) => set.id) ?? []).map(toSelectionKey)
            )
            const keepExisting = (initialData ?? []).filter((set) =>
              selectedIds.has(toSelectionKey(set.id))
            )
            const addNew = (addOnSets ?? []).filter(
              (set) =>
                selectedIds.has(toSelectionKey(set.id)) && !existingIds.has(toSelectionKey(set.id))
            )

            onConfirm?.([...keepExisting, ...addNew])
          }}
        >
          Apply Add-on Collections
        </Button>
      </div>
    </div>
  )
}
